import React from 'react';
import { useTranslation } from 'react-i18next';

interface LoadingSpinnerProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  text?: string;
  showText?: boolean;
}

/**
 * Consistent, always-visible loading spinner. Uses inline keyframes so the
 * animation never gets stuck and stays visible in light/dark themes.
 *
 * Sizes:
 * - xs: 16px (icon buttons, inline)
 * - sm: 24px (small buttons, compact cards)
 * - md: 36px (standard cards, modals)
 * - lg: 48px (page-level, large cards)
 * - xl: 64px (full-page loading)
 */
const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  text,
  showText = true,
}) => {
  const { t } = useTranslation();
  const displayText = text ?? t('common.loading');
  const sizeClasses = {
    xs: 'w-4 h-4 min-w-[1rem] min-h-[1rem] border-[2px]',
    sm: 'w-6 h-6 min-w-[1.5rem] min-h-[1.5rem] border-2',
    md: 'w-9 h-9 min-w-[2.25rem] min-h-[2.25rem] border-[3px]',
    lg: 'w-12 h-12 min-w-[3rem] min-h-[3rem] border-4',
    xl: 'w-16 h-16 min-w-[4rem] min-h-[4rem] border-4',
  };
  const textSizes = {
    xs: 'text-xs',
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-sm',
    xl: 'text-base',
  };
  const gapClasses = { xs: 'gap-2', sm: 'gap-2', md: 'gap-3', lg: 'gap-3', xl: 'gap-4' };
  const shouldShowText = showText && (size === 'md' || size === 'lg' || size === 'xl');

  const ringMinHeight = size === 'xl' ? 'min-h-[4rem]' : size === 'lg' ? 'min-h-[3rem]' : '';

  return (
    <div className={`flex flex-col items-center justify-center ${gapClasses[size]} min-h-0 ${ringMinHeight}`} role="status" aria-busy="true" aria-label={t('common.loading')}>
      <div
        className={`loading-spinner-ring ${sizeClasses[size]} rounded-full border-solid shrink-0 opacity-100`}
        style={{
          borderColor: 'var(--vscode-text-tertiary, rgba(0,0,0,0.15))',
          borderTopColor: 'var(--vscode-primary, #4f46e5)',
        }}
      />
      {shouldShowText && displayText && (
        <p className={`vscode-text-secondary ${textSizes[size]} font-medium`}>{displayText}</p>
      )}
    </div>
  );
};

export default LoadingSpinner;
